import type { RxDocument } from 'rxdb';

import {
  useRxDocuments,
  useRXDeleteByIds,
  useRxCreateMutation,
  useDuplicateMutation,
  useRxDocumentsLazy,
  useRxPatchMutation,
  useRxWriter,
  type MutationOptions,
  useRxDocument,
  useRxDocumentLazy,
} from '@/hooks/rxdb-base';

import type { Block } from '@flintmint/core-shared/rxdb';
import { createBlankEditorContent } from './use-notes';

export const DEFAULT_BLOCK_NAME = 'Untitled Block';

// create

export const useBlockCreate = () => {
  const { mutate: createMutation, isLoading } = useRxCreateMutation<Block>('blocks');

  const createWithDefaults = async (payload: Partial<Block>, mutationOptions?: MutationOptions) => {
    const blockData = {
      content: createBlankEditorContent(),
      name: DEFAULT_BLOCK_NAME,
      instructions: {},
      aliases: [],
      tagIds: [],
      ...payload,
    };

    return createMutation(blockData, mutationOptions);
  };

  return { mutate: createWithDefaults, isLoading };
};

export const useBlockDuplicate = () => useDuplicateMutation<Block>('blocks');

// read

export const useBlock = (id?: string) => useRxDocument<Block>('blocks', id);

export const useBlocks = (blockIds: string[] = []) => useRxDocuments<Block>('blocks', { ids: blockIds });

export const useBlockLazy = () => useRxDocumentLazy<Block>('blocks');

export const useBlocksLazy = () => useRxDocumentsLazy<Block>('blocks');

// update

export const useBlockPatch = () => useRxPatchMutation<Block>();

// upsert

export const useBlockUpsert = (blockRxDoc?: RxDocument<Block>) =>
  useRxWriter(blockRxDoc, undefined, {
    collectionName: 'blocks',
    create: useBlockCreate(),
    patch: useBlockPatch(),
  });

// delete

export const useBlocksDeleteByIds = () => useRXDeleteByIds<Block>('blocks');
